import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { SB } from "@/config/sidebarSizing";
import { THEME } from "@/config/theme";

interface MenuItem {
  label: string;
  onClick: () => void;
  danger?: boolean;
}

interface Props {
  items: MenuItem[];
}

export default function RowMenu({ items }: Props) {
  const [open, setOpen] = useState(false);
  // Popup is portaled to <body> so the sidebar's own overflow-hidden/overflow-y-auto
  // ancestors (Sidebar.tsx, WorkspaceList.tsx) can't clip it. Coords are the
  // fixed-position anchor under the trigger, captured when it opens — same
  // pattern as Toolbar.tsx's ToolGroupButton flyout.
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggleOpen = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        const r = triggerRef.current?.getBoundingClientRect();
        if (r) setCoords({ top: r.bottom + 2, left: r.right });
      }
      return next;
    });
  };

  return (
    <div ref={triggerRef} className="relative shrink-0">
      <button
        onClick={(e) => { e.stopPropagation(); toggleOpen(); }}
        className={`p-0.5 rounded transition-all ${THEME.panel.hoverText} ${THEME.panel.hover} ${
          open ? "opacity-100 bg-white/10" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        <MoreHorizontal size={SB.icon.rowMenu} />
      </button>
      {open && coords && createPortal(
        <div
          ref={menuRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, transform: "translateX(-100%)" }}
          className={`z-50 min-w-[148px] ${THEME.popup.container} rounded-lg shadow-xl py-1 flex flex-col overflow-hidden`}
        >
          {items.map((item) => (
            <button
              key={item.label}
              onClick={(e) => { e.stopPropagation(); setOpen(false); item.onClick(); }}
              className={`px-3 py-1.5 text-left text-[13px] transition-colors ${
                item.danger
                  ? "text-red-400 hover:bg-red-950/50"
                  : `${THEME.popup.text} ${THEME.popup.itemHover}`
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}
